import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import useActions, { useAppSelector } from "../../../app/hooks";
import { Dash } from "../../../types/Dash";
import { selectLibrary } from "../../library/librarySlice";
import CircularProgress from "@mui/material/CircularProgress";

export interface PlanStep {
  stepNumber: number;
  text: string;
  necessaryFiles: string[];
  necessaryCreateFiles: string[];
  execCommands: string[];
}

function parseStepsFromCode(code: string): PlanStep[] {
  const lines = code.split(/\r?\n/);
  const stepMarkers: { lineIndex: number; step: number }[] = [];

  lines.forEach((line, idx) => {
    const match = line.match(/```STEP\s+(\d+)/);
    if (match) {
      const stepNum = parseInt(match[1], 10);
      if (!Number.isNaN(stepNum)) {
        stepMarkers.push({ lineIndex: idx, step: stepNum });
      }
    }
  });

  const steps: PlanStep[] = [];
  const totalLines = lines.length;

  // No markers -> whole code is one step
  if (stepMarkers.length === 0) {
    const necessaryFiles: string[] = [];
    const necessaryCreateFiles: string[] = [];
    const execCommands: string[] = [];

    lines.forEach((line) => {
      const necMatch = line.match(/```NECESSARY:\s*(.+)/);
      if (necMatch && necMatch[1]) {
        necessaryFiles.push(necMatch[1].trim());
      }
      const necCreateMatch = line.match(/```NECESSARY_CREATE:\s*(.+)/);
      if (necCreateMatch && necCreateMatch[1]) {
        necessaryCreateFiles.push(necCreateMatch[1].trim());
      }
      const execMatch = line.match(/```EXEC:\s*(.+)/);
      if (execMatch && execMatch[1]) {
        execCommands.push(execMatch[1].trim());
      }
    });

    steps.push({
      stepNumber: 1,
      text: code,
      necessaryFiles,
      necessaryCreateFiles,
      execCommands,
    });

    return steps;
  }

  const markerIndices = stepMarkers.map((m) => m.lineIndex).sort((a, b) => a - b);
  const stepRanges: { stepNumber: number; start: number; end: number }[] = [];

  // Step 1: before first STEP marker
  const firstMarkerLine = markerIndices[0];
  stepRanges.push({ stepNumber: 1, start: 0, end: firstMarkerLine });

  // Subsequent steps between markers
  for (let i = 0; i < markerIndices.length; i += 1) {
    const start = markerIndices[i];
    const end = i + 1 < markerIndices.length ? markerIndices[i + 1] : totalLines;

    const line = lines[start];
    const match = line.match(/```STEP\s+(\d+)/);
    const explicit = match ? parseInt(match[1], 10) : NaN;
    const stepNumber = !Number.isNaN(explicit) ? explicit : i + 2;

    stepRanges.push({ stepNumber, start, end });
  }

  stepRanges.forEach((range) => {
    const slice = lines.slice(range.start, range.end);
    const text = slice.join("\n");

    const necessaryFiles: string[] = [];
    const necessaryCreateFiles: string[] = [];
    const execCommands: string[] = [];

    slice.forEach((line) => {
      const necMatch = line.match(/```NECESSARY:\s*(.+)/);
      if (necMatch && necMatch[1]) {
        necessaryFiles.push(necMatch[1].trim());
      }
      const necCreateMatch = line.match(/```NECESSARY_CREATE:\s*(.+)/);
      if (necCreateMatch && necCreateMatch[1]) {
        necessaryCreateFiles.push(necCreateMatch[1].trim());
      }
      const execMatch = line.match(/```EXEC:\s*(.+)/);
      if (execMatch && execMatch[1]) {
        execCommands.push(execMatch[1].trim());
      }
    });

    steps.push({
      stepNumber: range.stepNumber,
      text,
      necessaryFiles,
      necessaryCreateFiles,
      execCommands,
    });
  });

  return steps;
}

function extractCodeSet(stepText: string): string | null {
  // Look for code between ```code_set and ```
  const regex = /```code_set\s*([\s\S]*?)```/;
  const match = stepText.match(regex);
  if (match && match[1]) {
    return match[1].trim();
  }
  return null;
}

export function PlanProcessing(props: {
  open: boolean;
  code: string;
  dash: Dash | null;
  onClose: () => void;
}) {
  const { open, code, dash, onClose } = props;
  const actions = useActions();
  const librarySlice = useAppSelector(selectLibrary);
  const [creatingFiles, setCreatingFiles] = useState<Set<string>>(new Set());

  const steps = useMemo(() => parseStepsFromCode(code || ""), [code]);

  // Clear creating files when status returns to idle (file creation complete)
  useEffect(() => {
    if (librarySlice.status === "idle" && creatingFiles.size > 0) {
      // Clear all creating files after a short delay to allow UI to update
      setTimeout(() => {
        setCreatingFiles(new Set());
      }, 500);
    }
  }, [librarySlice.status, creatingFiles.size]);

  const handleGoClick = (fileName: string, stepText: string, codeSet: string) => {
    if (!dash || !dash.repo_id) {
      alert("No repository associated with this workbook");
      return;
    }

    // Extract code_set block - should already be extracted, but double-check
    const extractedCode = extractCodeSet(stepText);
    if (!extractedCode) {
      alert("No code_set block found for this file");
      return;
    }

    // Extract folder path from file name
    const lastSlash = fileName.lastIndexOf("/");
    const filePath = lastSlash > 0 ? fileName.substring(0, lastSlash) : "/";
    const justFileName = lastSlash > 0 ? fileName.substring(lastSlash + 1) : fileName;

    // Add to creating set
    setCreatingFiles((prev) => new Set(prev).add(justFileName));

    // Create the file with content using the new saga
    actions.createRepoFileWithContent({
      repoId: dash.repo_id,
      file_name: fileName,
      file_path: filePath,
      codeSet: extractedCode,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Execution Steps</DialogTitle>
      <DialogContent dividers>
        {steps.length === 0 ? (
          <Typography variant="body2">No steps detected in the current code.</Typography>
        ) : (
          <List>
            {steps.map((step) => (
              <ListItem
                key={`step_${step.stepNumber}`}
                alignItems="flex-start"
                sx={{ flexDirection: "column", alignItems: "stretch" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Step {step.stepNumber}
                </Typography>
                {(step.necessaryFiles.length > 0 ||
                  step.necessaryCreateFiles.length > 0 ||
                  step.execCommands.length > 0) && (
                  <Box sx={{ mb: 1 }}>
                    {step.necessaryFiles.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Modifies file(s):
                        </Typography>
                        {step.necessaryFiles.map((fileName, idx) => {
                          const codeSet = extractCodeSet(step.text);
                          const hasCodeSet = codeSet !== null;
                          const isCreating = creatingFiles.has(fileName.split("/").pop() || fileName);
                          
                          return (
                            <Box
                              key={`modify_${step.stepNumber}_${idx}`}
                              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                            >
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {fileName}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleGoClick(fileName, step.text, codeSet || "")}
                                disabled={!hasCodeSet || isCreating}
                              >
                                {isCreating ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  "CREATE INSTEAD"
                                )}
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {step.necessaryCreateFiles.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Creates file(s):
                        </Typography>
                        {step.necessaryCreateFiles.map((fileName, idx) => {
                          const codeSet = extractCodeSet(step.text);
                          const hasCodeSet = codeSet !== null;
                          const isCreating = creatingFiles.has(fileName.split("/").pop() || fileName);
                          
                          return (
                            <Box
                              key={`create_${step.stepNumber}_${idx}`}
                              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                            >
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {fileName}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleGoClick(fileName, step.text, codeSet || "")}
                                disabled={!hasCodeSet || isCreating}
                              >
                                {isCreating ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  "GO"
                                )}
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {step.execCommands.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Commands to execute:
                        </Typography>
                        {step.execCommands.map((cmd, idx) => (
                          <Box
                            key={`exec_${step.stepNumber}_${idx}`}
                            component="pre"
                            sx={{
                              m: 0,
                              mt: 0.5,
                              p: 0.5,
                              bgcolor: "grey.200",
                              borderRadius: 1,
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {cmd}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 240,
                    overflow: "auto",
                  }}
                >
                  {step.text}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

